import { FleetDocumentUpload } from "../documents/types";
import { transformFleetVehicle } from "../transformations";
import { initialFleetVehicleStatusDetailForm } from "../consts";
import { FleetVehicleStatusDetailFormValues } from "../types";
import { isVehicleLocationComplete } from "../locations/consts";
import { VehicleLocationFormValues } from "../locations/types";
import {
  FleetVehicleService,
  FleetVehicleServiceEditFormValues,
  VehicleServiceDamage,
  VehicleServiceExpense,
  VehicleServiceStatus,
} from "./types";

export const transformVehicleServiceStatus = (res: any): VehicleServiceStatus => ({
  id: res.id,
  name: res.name,
  color: res.color,
});

const transformVehicleServiceDamage = (res: any): VehicleServiceDamage => ({
  id: res.id,
  name: res.name,
  description: res.description ?? null,
  damageDate: res.damageDate ? new Date(res.damageDate) : null,
  vehicleDamageStatusId: res.vehicleDamageStatusId ?? null,
  vehicleDamageStatusName: res.vehicleDamageStatusName ?? null,
  vehicleDamageStatusColor: res.vehicleDamageStatusColor ?? null,
});

const transformVehicleServiceExpense = (res: any): VehicleServiceExpense => ({
  id: res.id,
  name: res.name,
  expenseTypeId: res.expenseTypeId ?? null,
  expenseTypeName: res.expenseTypeName ?? null,
  expenseDate: res.expenseDate ? new Date(res.expenseDate) : null,
  amount: res.amount ?? null,
  invoiceIncomingItemId: res.invoiceIncomingItemId ?? null,
});

export const transformFleetVehicleService = (res: any): FleetVehicleService => ({
  id: res.id,
  vehicleServiceStatus: res.vehicleServiceStatus
    ? transformVehicleServiceStatus(res.vehicleServiceStatus)
    : null,
  vehicle: res.vehicle ? transformFleetVehicle(res.vehicle) : null,
  contactId: res.contactId ?? null,
  contactName: res.contactName ?? null,
  name: res.name,
  description: res.description ?? null,
  receivedDate: res.receivedDate ? new Date(res.receivedDate) : null,
  expectedDate: res.expectedDate ? new Date(res.expectedDate) : null,
  completedDate: res.completedDate ? new Date(res.completedDate) : null,
  isOpen: res.isOpen ?? false,
  kmTraveled: res.kmTraveled ?? null,
  created: res.created ? new Date(res.created) : null,
  userCreatedName: res.userCreatedName ?? null,
  damages: (res.damages ?? []).map(transformVehicleServiceDamage),
  expenses: (res.expenses ?? []).map(transformVehicleServiceExpense),
  totalExpenses: res.totalExpenses ?? 0,
  documentUploads: res.documentUploads ?? [],
});

/** Detalj naloga u obrazac čarobnjaka. */
export const transformFleetVehicleServiceEditFormValues = (
  res: FleetVehicleService
): FleetVehicleServiceEditFormValues => ({
  id: res.id,
  vehicleServiceStatusId: res.vehicleServiceStatus?.id ?? "",
  contactId: res.contactId,
  name: res.name,
  description: res.description,
  receivedDate: res.receivedDate,
  expectedDate: res.expectedDate,
  completedDate: res.completedDate,
  kmTraveled: res.kmTraveled,
  damageIds: res.damages.map((damage) => damage.id),
  documentUploads: res.documentUploads,
});

/**
 * Polja statusa koja nalog već nosi (serviser, datumi, kilometraža, opis, lokacija) — bez
 * bloka `service`, pa se koristi kad nalog već postoji i treba samo promijeniti status.
 */
export const buildServiceStatusFields = (
  service: FleetVehicleServiceEditFormValues,
  location: VehicleLocationFormValues | null
): FleetVehicleStatusDetailFormValues => ({
  ...initialFleetVehicleStatusDetailForm,
  contactId: service.contactId,
  validFrom: service.receivedDate,
  validUntil: service.expectedDate,
  note: service.description,
  kmTraveled: service.kmTraveled,
  // Nepotpunu lokaciju backend odbija, a nalog se može unijeti i bez premještaja.
  location: isVehicleLocationComplete(location) ? location : null,
});

/**
 * Statusni detalj za "Nedostupno – Servis" složen iz naloga. Nalog nosi sve što status
 * treba (serviser, datumi, kilometraža, opis), pa se ne pita dvaput — status i nalog idu
 * jednim zahtjevom, u istoj transakciji.
 *
 * Koriste ga oba puta do servisa: promjena statusa preko značke i premještaj na servis.
 */
export const buildServiceStatusDetail = (
  service: FleetVehicleServiceEditFormValues,
  location: VehicleLocationFormValues | null
): FleetVehicleStatusDetailFormValues => ({
  ...buildServiceStatusFields(service, location),
  service,
});

/**
 * Tijelo zahtjeva za nalog. Isti oblik ide na vlastiti endpoint i kao blok `service`
 * uz promjenu statusa vozila, pa se sastavlja na jednom mjestu.
 */
export const transformFleetVehicleServiceOnSave = (
  formValues: FleetVehicleServiceEditFormValues,
  documentUploads: FleetDocumentUpload[]
): any => ({
  vehicleServiceStatusId: formValues.vehicleServiceStatusId,
  contactId: formValues.contactId,
  name: formValues.name,
  description: formValues.description,
  receivedDate: formValues.receivedDate,
  expectedDate: formValues.expectedDate,
  completedDate: formValues.completedDate,
  kmTraveled: formValues.kmTraveled,
  documentUploadIds: documentUploads.map((upload) => upload.id),
  damageIds: formValues.damageIds,
});
